import React, { useEffect } from 'react';

interface IConfirmDialog {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  /**
   * 본문에 단순 텍스트가 아닌 리치 컨텐츠(링크, 버튼 등)가 필요할 때 사용.
   * 주어지면 message 대신 렌더됨.
   */
  messageNode?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
}

/**
 * @author taulmax
 * @date 2024.12.19
 * @description 확인 다이얼로그 컴포넌트
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  messageNode,
  confirmText = '확인',
  cancelText = '취소',
  type = 'info'
}: IConfirmDialog) => {
  // ESC 키 이벤트 처리
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          icon: '!text-red-600',
          confirmButton: '!bg-red-600 hover:!bg-red-700'
        };
      case 'warning':
        return {
          icon: '!text-amber-600',
          confirmButton: '!bg-amber-600 hover:!bg-amber-700'
        };
      default:
        return {
          icon: '!text-blue-600',
          confirmButton: '!bg-blue-600 hover:!bg-blue-700'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <>
      {/* 배경 오버레이 */}
      <div 
        className="!fixed !inset-0 !bg-black !bg-opacity-50 !z-50"
        onClick={onClose}
      />
      
      {/* 다이얼로그 */}
      <div className="!fixed !inset-0 !z-50 !flex !items-center !justify-center !p-4">
        <div className="!bg-white !rounded-lg !shadow-xl !max-w-md !w-full !animate-fadeIn !relative">
          {/* X 버튼 */}
          <div className="!absolute !top-4 !right-4">
            <button
              onClick={onClose}
              className="!p-1 !rounded-lg hover:!bg-gray-100 !transition-colors"
            >
              <svg className="!w-5 !h-5 !text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* 헤더 */}
          <div className="!p-6 !pb-4">
            <div className="!flex !items-start">
              <div className={`!mx-auto !flex-shrink-0 !flex !items-center !justify-center !h-12 !w-12 !rounded-full !bg-gray-100 ${styles.icon}`}>
                {type === 'danger' && (
                  <svg className="!h-6 !w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                )}
                {type === 'warning' && (
                  <svg className="!h-6 !w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {type === 'info' && (
                  <svg className="!h-6 !w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
            </div>
            <div className="!mt-3 !text-center">
              <h3 className="!text-lg !font-medium !text-gray-900">
                {title}
              </h3>
              <div className="!mt-2">
                {messageNode ? (
                  <div className="!text-sm !text-gray-500">{messageNode}</div>
                ) : (
                  <p className="!text-sm !text-gray-500 !whitespace-pre-line">
                    {message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* 액션 버튼 */}
          <div className="!bg-gray-50 !px-6 !py-4 !flex !gap-3 !justify-end !rounded-b-lg">
            <button
              onClick={onClose}
              className="!px-4 !py-2 !bg-white !border !border-gray-300 hover:!bg-gray-50 !text-gray-700 !text-sm !font-medium !rounded-md !transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={`!px-4 !py-2 !text-white !text-sm !font-medium !rounded-md !transition-colors ${styles.confirmButton}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmDialog;